/**
 * StateClimateSnapshot — /states/climate-snapshot
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";

export default function StateClimateSnapshot() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { stateId, geography: geo, stateParams } = useStateContext();

  useEffect(() => {
    client.get(`/states/climate-snapshot${stateParams}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [geo]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="State Climate Snapshot"
        subtitle={`${geo} · Aggregated emissions and reduction potential`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          label="Total Footprint"
          value={(data?.total_footprint_tco2e || 0).toFixed(0)}
          unit="tCO₂e"
          sub="Aggregate Scope 1+2"
          color="text-teal-700"
        />
        <MetricCard
          label="Reduction Potential"
          value={(data?.total_reduction_potential_tco2e || 0).toFixed(0)}
          unit="tCO₂e"
          sub="From projects"
          color="text-emerald-700"
        />
        <MetricCard
          label="Abatement Potential"
          value={`${data?.abatement_potential_pct || 0}%`}
          sub="Of total footprint"
          color="text-blue-700"
        />
      </div>

      <SectionCard title="Sector Breakdown" noPadding>
        {(data?.sector_breakdown || []).map((s) => (
          <div key={s.sector} className="flex items-center px-5 py-3 border-b border-slate-50 last:border-0">
            <p className="text-sm text-slate-700 flex-1 capitalize">{s.sector || "Other"}</p>
            <div className="flex items-center gap-3">
              <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-400 rounded-full" style={{ width: `${Math.min(s.share_pct, 100)}%` }} />
              </div>
              <p className="text-sm font-semibold text-slate-700 w-16 text-right tabular-nums">{s.tco2e?.toFixed(0)} t</p>
            </div>
          </div>
        ))}
      </SectionCard>

      {data?.top_emitters?.length > 0 && (
        <SectionCard title="Top Emitters" noPadding>
          {data.top_emitters.map((e, i) => (
            <div key={i} className="flex items-center px-5 py-3 border-b border-slate-50 last:border-0">
              <span className="text-xs font-bold text-slate-400 w-6">{i + 1}.</span>
              <p className="text-sm text-slate-700 flex-1">{e.org_name}</p>
              <p className="text-sm font-semibold text-amber-700 tabular-nums">{(e.tco2e || 0).toFixed(1)} tCO₂e</p>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}
